import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { EqualMark, Eyebrow, StepNumber } from "@/components/brand";
import { StudioProjectsPanel } from "@/components/StudioProjectsPanel";
import { RoadmapAdminPanel } from "@/components/RoadmapAdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cards = [
  {
    id: "upload",
    path: "/admin/upload",
    label: "Knowledge Base",
    sub: "Upload transcripts, PDFs, videos and link YouTube sessions",
    testId: "upload-documents-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    ),
  },
  {
    id: "chat",
    path: "/admin/test-chat",
    label: "Test AI",
    sub: "Preview responses before your clients see them",
    testId: "test-chat-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    id: "analytics",
    path: "/admin/analytics",
    label: "Analytics",
    sub: "Track engagement, knowledge gaps and program metrics",
    testId: "analytics-dashboard-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: "conversations",
    path: "/admin/conversations",
    label: "Conversations",
    sub: "Read all client sessions and export transcripts",
    testId: "all-conversations-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    id: "flagged",
    path: "/admin/flagged-messages",
    label: "Flagged",
    sub: "Respond to clients who requested human assistance",
    testId: "flagged-messages-card",
    accent: true,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
      </svg>
    ),
  },
  {
    id: "comps",
    path: "/comps",
    label: "Market Data",
    sub: "Lease comps, benchmarks and applicant submissions",
    testId: "comp-database-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13a4 4 0 014-4h10a4 4 0 014 4M3 13v6a2 2 0 002 2h14a2 2 0 002-2v-6M3 13l2-7a2 2 0 012-1.5h10A2 2 0 0119 6l2 7" />
      </svg>
    ),
  },
  {
    id: "invites",
    path: "/admin/invites",
    label: "Invitations",
    sub: "Invite staff and applicants, track who accepted",
    testId: "invites-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: "studio",
    path: "/studio",
    label: "Design Studio",
    sub: "Floor plans, LBMC 5.92 checks, budget and architect hand-off",
    testId: "design-studio-card",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 6h16M10 4v16" />
      </svg>
    ),
  },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [cityEmail, setCityEmail] = useState("");
  const [cityName, setCityName] = useState("City of Long Beach");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("equal_admin_welcomed")) {
      setShowWelcome(true);
    }
    axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCityEmail(res.data.city_official_email || "");
        setCityName(res.data.city_official_name || "City of Long Beach");
        setSettingsLoaded(true);
      }).catch(() => setSettingsLoaded(true));
    // eslint-disable-next-line
  }, [token]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning.";
    if (hour < 17) return "Good afternoon.";
    return "Good evening.";
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`,
        { city_official_email: cityEmail, city_official_name: cityName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSavingSettings(false); }
  };

  const dismissWelcome = () => { setShowWelcome(false); localStorage.setItem("equal_admin_welcomed", "1"); };

  return (
    <AdminShell active="dashboard" logoutTestId="admin-logout-button">
      <div className="mb-12">
        <Eyebrow className="mb-3">Control Panel</Eyebrow>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">
          {getGreeting()}
        </h1>
        <p className="text-mute mt-4 text-base max-w-xl">Your knowledge base is live. What would you like to do?</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <button key={card.id} onClick={() => navigate(card.path)}
            className="card-flat p-5 text-left group hover:border-[#b9c4d0] hover:-translate-y-px transition-[border-color,transform] duration-200 animate-rise-in"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={card.testId}>
            <div className="flex items-start justify-between">
              <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center mb-5 ${card.accent ? "bg-[#fdecea] text-[#c0392b]" : "bg-mint text-equal-dark"}`}>
                {card.icon}
              </div>
              <span className="font-mono text-[11px] text-mute/70">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <p className="text-ink font-semibold text-[14.5px] mb-1 tracking-tight">{card.label}</p>
            <p className="text-mute text-xs leading-relaxed">{card.sub}</p>
            <div className="mt-4 flex items-center gap-1 eyebrow group-hover:text-equal-dark transition-colors">
              <span>Open</span>
              <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Settings Panel */}
      {settingsLoaded && (
        <div className="mt-12 card-flat p-6 md:p-8 max-w-2xl">
          <Eyebrow className="mb-6">Platform Settings</Eyebrow>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-ink mb-1">
                City Official Email
              </label>
              <p className="text-xs text-mute mb-2">Session transcripts are sent here when a client ends their session.</p>
              <input
                type="email"
                placeholder="e.g. evelyn.amado@longbeach.gov"
                value={cityEmail}
                onChange={e => setCityEmail(e.target.value)}
                className="input-flat"
                data-testid="city-official-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">
                City Official Name
              </label>
              <input
                type="text"
                placeholder="City of Long Beach"
                value={cityName}
                onChange={e => setCityName(e.target.value)}
                className="input-flat"
                data-testid="city-official-name-input"
              />
            </div>
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="btn-primary h-9 px-5"
              data-testid="save-settings-button"
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      )}

      <StudioProjectsPanel />
      <RoadmapAdminPanel />

      {/* First-time admin welcome modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-ink/70">
          <div className="w-full max-w-lg rounded-2xl overflow-hidden bg-white border border-line max-h-[90vh] flex flex-col animate-rise-in">
            {/* Header band */}
            <div className="px-8 py-7 flex-shrink-0 bg-ink text-white">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <EqualMark size={28} color="#fff" />
                  <span className="font-bold text-xl tracking-tight">Welcome to Equal</span>
                </div>
                <button onClick={dismissWelcome}
                  className="text-white/60 hover:text-white transition-colors flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                24/7 AI guidance for Long Beach cannabis equity applicants, built by Tkach Ventures.
              </p>
            </div>

            {/* Scrollable features */}
            <div className="px-8 py-6 space-y-4 overflow-y-auto flex-1 bg-page">
              <Eyebrow>As an Admin, you can</Eyebrow>
              <div className="space-y-2">
                {[
                  { title: "Manage the Knowledge Base", body: "Upload transcripts, PDFs and videos — the AI answers only from these." },
                  { title: "Review All Conversations", body: "Read and export every applicant session." },
                  { title: "Respond to Flagged Messages", body: "Reply in-app when an applicant asks for a human." },
                  { title: "Track Analytics", body: "Top topics, knowledge gaps, engagement." },
                  { title: "Configure the Platform", body: "Set the City Official email for session transcripts." },
                ].map((item, i) => (
                  <div key={item.title} className="flex gap-3 p-3 rounded-[10px] bg-white border border-line">
                    <span className="mt-0.5"><StepNumber n={i + 1} active={i === 0} /></span>
                    <div>
                      <p className="text-sm font-semibold text-ink">{item.title}</p>
                      <p className="text-xs text-mute mt-0.5 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sticky footer */}
            <div className="px-8 py-6 flex-shrink-0 border-t border-line">
              <button onClick={dismissWelcome} className="btn-primary w-full h-11">
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
